import React from 'react'
import PaymentMethodForm, { type PaymentMethodFormHandle } from '../../common/components/payment-method-new/PaymentMethodForm'
import { type WebCart, type WebCustomer2, type WebPaymentMethod2, type StripeCard } from '../../common/types/webshop.types'
import useCreatePaymentMethod from '../../common/hooks/useCreatePaymentMethod'
import { getPaymentIntent } from '../../common/js/stripe-utility'
import { fetchLogin, saveCustomerAddress } from '../../common/js/bfx-customer-utility'
import useBlackthornCapture from '../../common/hooks/useBlackthornCapture'
import useStripeCapture from '../../common/hooks/useStripeCapture'
import { axiosPost } from '../../common/js/axios-utility'
import { capturePayPalOrder, createPayPalOrder, getPayPalGatewayByCurrency, hasTestPrivileges } from '../../common/js/paypal-utility'
import { hasAmountToPay } from '../../common/js/cart-utility'
import SpinnerOverlay from '../../common/components/SpinnerOverlay'
import { errorToast } from '../../common/js/toast-utility'
import { useQueryClient } from '@tanstack/react-query'

import { useTranslation } from 'react-i18next'
import BillingAddressForm from '../../common/components/BillingAddressForm'
import Button from '../../common/components/Button'

type Props = {
    order: WebCart,
    customer: WebCustomer2,
}

export default function PaylinkBilling({ order, customer }: Props) {
    const { saveCard } = useCreatePaymentMethod();
    const [submitting, setSubmitting] = React.useState(false);
    const paymentMethod = React.useRef<WebPaymentMethod2>(null);
    const [page, setPage] = React.useState('card');
    const [partialFailure, setPartialFailure] = React.useState(false);

    const {t} = useTranslation('checkout');

    const paymentMethodFormRef = React.useRef<PaymentMethodFormHandle>(null);
    const billingAddressFormRef = React.useRef<HTMLFormElement>(null);

    const queryClient = useQueryClient();

    React.useEffect(() => {
        if (customer != null && (customer.customerId == null || customer.customerId == '')){
            setPage('billing');
        }
    }, [customer])

    const handleSubmit = async (data: any) => {
        if (page == 'billing'){
            await handleBillingSubmit();
        }
        else if (page == 'card'){
            await handlePaymentSubmit(data);
        }
    }

    const handleBillingSubmit = async () => {
        setSubmitting(true);
        try{
            const form = billingAddressFormRef.current;
            if (!form.checkValidity()){
                form.reportValidity();
                return;
            }
            else{
                await saveCustomerAddress(form, customer);
                queryClient.invalidateQueries({queryKey: ['paylink', 'customer']});
                setPage('card');
            }
        }
        catch(err){
            console.error(err);
            errorToast('Failed to save address. Please review your information and try again.')
        }
        finally{
            setSubmitting(false);
        }
    }

    const handleCreatePayPalOrder = async (): Promise<{ orderId: string }> => {
        const gateway = getPayPalGatewayByCurrency(order.currencyIso, hasTestPrivileges(customer));
        return createPayPalOrder(gateway, order);
    }

    const handlePayPalPaymentSubmit = async (paypalOrderId: string) => {
        setSubmitting(true);
        const gateway = getPayPalGatewayByCurrency(order.currencyIso, hasTestPrivileges(customer));
        try {
            // skipFulfillment: paylink runs its own fulfillment via /paylink/2/submit below.
            await capturePayPalOrder(gateway, paypalOrderId, order.id, order.total, order.currencyIso, true);
        } catch (err) {
            handlePaymentError(err);
            return;
        }
        try {
            await axiosPost('/paylink/2/submit', buildPayload());
            window.location.href = `/account/order/?id=${order.id}`;
        } catch (err) {
            handlePartialFailure(err);
        }
    }

    const handlePartialFailure = (error: any) => {
        console.error('Paylink finalization failed after payment capture:', error);
        setPartialFailure(true);
        setSubmitting(false);
    }

    const handlePaymentSubmit = async (data: any) => {
        if (!hasAmountToPay(order)) {
            errorToast(t('billing.invalidOrder'));
            return;
        }
        if (data.selectedId?.startsWith('paypal:')) {
            await handlePayPalPaymentSubmit(data.selectedId.replace('paypal:', ''));
            return;
        }
        setSubmitting(true);
        try {
            const login = fetchLogin();
            if (data.isNewCard) {
                paymentMethod.current = await saveCard(data, customer);
            }
            else {
                paymentMethod.current = customer.paymentMethods.find((c: WebPaymentMethod2) => {
                    return c.id == data.selectedId;
                })
            }

            const pm = paymentMethod.current as StripeCard;
            if (pm.isBlackthorn) {
                let intent: any = getCachedIntent();
                if (!intent) {
                    intent = await getPaymentIntent(order, customer, login);
                    setCachedIntent(intent);
                }
                await capturePaylink(customer, order, pm);
            }
            else {
                await captureStripe(customer, order, pm, false);
                await handlePaymentComplete();
            }
        }
        catch(err) {
            handlePaymentError(err);
        }
    }

    const handlePaymentComplete = async () => {
        try {
            const isBlackthorn = (paymentMethod.current as StripeCard)?.isBlackthorn;
            const payload = isBlackthorn ? buildBlackthornPayload() : buildPayload();
            await axiosPost('/paylink/2/submit', payload);
            window.location.href = `/account/order/?id=${order.id}`
        }
        catch(err) {
            handlePartialFailure(err);
        }
    }

    const handlePaymentError = (error: any) => {
        queryClient.invalidateQueries({queryKey: ['paylink', 'customer']});
        let message = 'Payment Failed. Please review your info and try again.';
        
        try{
            if(error.response && error.response.data){
                message = 'Payment Failed: ' + error.response.data.error.replaceAll('"', '');
            }
        }
        catch(err){}
        paymentMethodFormRef.current.resetForm();
        errorToast(message)
        setSubmitting(false);
    }

    const buildBlackthornPayload = () => {
        var request = {
            opportunityId : order.id,
            transactionId : getTransactionId(),
            accountId : customer.accountId,
            paymentMethodId : paymentMethod.current.id,
            email : customer.email
        }
        return request;
    }

    // A direct-Stripe or PayPal payment is already recorded by its capture, so no transactionId
    // or paymentMethodId is needed; Apex RestPaylink resolves them from the Opportunity.
    const buildPayload = () => ({
        opportunityId: order.id,
        accountId: customer.accountId,
        email: customer.email,
        isBlackthornPayment: false,
    })

    const {
        getIntent: getCachedIntent,
        setIntent: setCachedIntent,
        getTransactionId,
        capturePaylink} = useBlackthornCapture(handlePaymentComplete, handlePaymentError);
    const { captureStripe } = useStripeCapture();

    let formContent = <></>
    if (partialFailure){
        formContent = (
            <div className='payment-method-container box box-accent'>
                <h3>{t('purchase.paylinkPartialFailureHeading')}</h3>
                <p>{t('purchase.paylinkPartialFailure')}</p>
            </div>
        )
    }
    else if (page == 'billing'){
        formContent = (
            <div className='payment-method-container box box-accent'>
                <BillingAddressForm customer={customer} formRef={billingAddressFormRef} />
                <Button className='btn-new btn-new-dynamic' label='Save Address' onClick={handleBillingSubmit} />
            </div>
        )
    }
    else if (page == 'card'){
        formContent = (
            <PaymentMethodForm
                ref={paymentMethodFormRef}
                customer={customer}
                submitButtonLabel={t('actions.pay')}
                onSubmit={handlePaymentSubmit}
                createPayPalOrder={handleCreatePayPalOrder}
                paypalAvailable={hasAmountToPay(order)} />
        )
    }

    let spinnerMessage = (
        page == 'card' ?
        t('purchase.submittingPayment') : 
        t('paymentMethod.savingPaymentMethod')
    );
    spinnerMessage += '...';

    return (
        <div>
            <SpinnerOverlay hidden={!submitting} fullscreen opaque message={spinnerMessage} size={125} />
            {formContent}
        </div>
    )
}

